import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useLanguage } from '../../i18n';
import styles from './SearchResults.module.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function SearchResults() {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';

  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Filter states (multi-select via checkboxes)
  const [selectedTypes, setSelectedTypes] = useState(
    searchParams.getAll('type') || []
  );
  const [selectedLanguages, setSelectedLanguages] = useState(
    searchParams.getAll('language') || []
  );
  const [selectedRegions, setSelectedRegions] = useState(
    searchParams.getAll('region') || []
  );

  const fetchResults = async () => {
    setLoading(true);
    setError(null);

    try {
      // Build query string from current states
      const params = new URLSearchParams();
      if (query.trim()) params.set('q', query.trim());

      selectedTypes.forEach(t => params.append('type', t));
      selectedLanguages.forEach(l => params.append('language', l));
      selectedRegions.forEach(r => params.append('region', r));

      const url = `${API_URL}/search?${params.toString()}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setResults(data);
    } catch (err) {
      setError(err.message || 'Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount + whenever URL params change
  useEffect(() => {
    fetchResults();
  }, [searchParams]);

  // Handle main search bar submit / Enter
  const handleSearch = (e) => {
    e.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;

    // Reset filters on new keyword search (optional - remove if you want to keep them)
    setSelectedTypes([]);
    setSelectedLanguages([]);
    setSelectedRegions([]);

    setSearchParams({ q: trimmed });
  };

  // Apply filters button
  const applyFilters = () => {
    const params = new URLSearchParams();
    if (query.trim()) params.set('q', query.trim());

    selectedTypes.forEach(t => params.append('type', t));
    selectedLanguages.forEach(l => params.append('language', l));
    selectedRegions.forEach(r => params.append('region', r));

    setSearchParams(params);
    setShowFilters(false); // close mobile filters
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedTypes([]);
    setSelectedLanguages([]);
    setSelectedRegions([]);
    setSearchParams(query.trim() ? { q: query.trim() } : {});
  };

  return (
    <main className={styles.main}>
      <div className={styles.container}>
        {/* Header */}
        <header className={styles.pageHeader}>
          <div className={styles.headerTop}>
            <Link to="/" className={styles.backLink}>
              <Icon icon="lucide:arrow-left" />
              {t('backToHome')}
            </Link>
            <h1 className={styles.pageTitle}>{t('searchResultsTitle')}</h1>
          </div>

          <p className={styles.resultsCount}>
            {loading ? t('searching') : t('resultsFor', { count: results.length, query: initialQuery || 'all' })}
          </p>

          {/* Working Search Bar */}
          <form onSubmit={handleSearch} className={styles.searchBar}>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('searchPlaceholder')}
              className={styles.searchInput}
            />
            <button type="submit" className={styles.searchBtn}>
              <Icon icon="lucide:search" />
            </button>
          </form>
        </header>

        {/* Filters Toggle (mobile) */}
        <button
          className={styles.filterToggle}
          onClick={() => setShowFilters(!showFilters)}
        >
          <Icon icon="lucide:filter" />
          {showFilters ? t('hideFilters') : t('showFilters')}
        </button>

        <div className={`${styles.filters} ${showFilters ? styles.filtersOpen : ''}`}>
          <div className={styles.filterGroup}>
                      <h3>{t('resourceType')}</h3>
                      {['Guide', 'Template', 'Policy', 'Contact'].map(typeOption => (
                        <label key={typeOption}>
                <input
                  type="checkbox"
                            checked={selectedTypes.includes(typeOption)}
                  onChange={(e) => {
                    setSelectedTypes(prev =>
                                e.target.checked ? [...prev, typeOption] : prev.filter(x => x !== typeOption)
                    );
                  }}
                />
                          {typeOption}
              </label>
            ))}
          </div>

          <div className={styles.filterGroup}>
                      <h3>{t('languageLabel')}</h3>
            {['English', 'Español', 'Français', 'العربية'].map(l => (
              <label key={l}>
                <input
                  type="checkbox"
                  checked={selectedLanguages.includes(l)}
                  onChange={(e) => {
                    setSelectedLanguages(prev =>
                      e.target.checked ? [...prev, l] : prev.filter(x => x !== l)
                    );
                  }}
                />
                {l}
              </label>
            ))}
          </div>

          <div className={styles.filterGroup}>
                      <h3>{t('regionLabel')}</h3>
            {['Global', 'Africa', 'Latin America', 'Asia-Pacific'].map(r => (
              <label key={r}>
                <input
                  type="checkbox"
                  checked={selectedRegions.includes(r)}
                  onChange={(e) => {
                    setSelectedRegions(prev =>
                      e.target.checked ? [...prev, r] : prev.filter(x => x !== r)
                    );
                  }}
                />
                {r}
              </label>
            ))}
          </div>

          <div className={styles.filterActions}>
            <button className={styles.applyFilters} onClick={applyFilters}>
                        {t('applyFilters')}
            </button>
            <button className={styles.clearFilters} onClick={clearFilters}>
                        {t('clearAll')}
            </button>
          </div>
        </div>

        {/* Results Grid */}
        <section className={styles.resultsGrid}>
          {loading ? (
            <div className={styles.loading}>
              <Icon icon="lucide:loader-2" className={styles.spinner} />
                        {t('searching')}
            </div>
          ) : error ? (
            <div className={styles.error}>
              <Icon icon="lucide:alert-triangle" />
              {error}
            </div>
          ) : results.length === 0 ? (
            <div className={styles.empty}>
              <Icon icon="lucide:search" />
                        <p>{t('noResultsFound', { query: initialQuery || 'all resources' })}</p>
            </div>
          ) : (
            results.map((res) => (
              <Link to={`/resource/${res.id}`} key={res.id} className={styles.resultCard}>
                <div className={styles.cardImagePlaceholder}></div>

                <div className={styles.cardBody}>
                  <div className={styles.cardTags}>
                    <span className={styles.tag}>{res.type}</span>
                  </div>

                  <h3 className={styles.cardTitle}>{res.title}</h3>
                  <p className={styles.cardDescription}>
                    {res.description.length > 120
                      ? res.description.substring(0, 120) + '...'
                      : res.description}
                  </p>

                  <div className={styles.cardMeta}>
                    <span>From: {res.source || 'Community'}</span>
                    <span>
                      {res.rating ? `${res.rating} (${res.reviewCount || 0} reviews)` : 'No ratings'}
                    </span>
                    <span>{res.languages?.join(', ') || 'N/A'}</span>
                    <span>{res.fileSize || res.fileType || 'N/A'}</span>
                  </div>

                  <div className={styles.cardFooter}>
                    <button className={styles.actionBtnSmall}>
                      <Icon icon="lucide:download" />
                      Download
                    </button>
                    <button className={styles.actionBtnIcon}>
                      <Icon icon="lucide:bookmark-plus" />
                    </button>
                  </div>
                </div>
              </Link>
            ))
          )}
        </section>

        {/* Load More – placeholder */}
        {!loading && results.length > 0 && (
          <div className={styles.loadMore}>
            <button className={styles.loadMoreBtn}>
              Load More Results
              <Icon icon="lucide:arrow-down" />
            </button>
            <p className={styles.paginationInfo}>
              Showing {results.length} results
            </p>
          </div>
        )}
      </div>
    </main>
  );
}
// import { useState, useEffect } from 'react';
// import { Link, useSearchParams } from 'react-router-dom';
// import { Icon } from '@iconify/react';
// import styles from './SearchResults.module.css';

// const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// export default function SearchResults() {
//   const [searchParams, setSearchParams] = useSearchParams();
//   const initialQuery = searchParams.get('q') || '';

//   const [query, setQuery] = useState(initialQuery);
//   const [results, setResults] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [showFilters, setShowFilters] = useState(false);

//   const fetchResults = async (searchQuery) => {
//     setLoading(true);
//     setError(null);

//     try {
//       const url = searchQuery
//         ? `${API_URL}/search?q=${encodeURIComponent(searchQuery)}`
//         : `${API_URL}/resources`; // fallback to all if empty query

//       const res = await fetch(url);
//       if (!res.ok) throw new Error(`HTTP ${res.status}`);
//       const data = await res.json();
//       setResults(data);
//     } catch (err) {
//       setError(err.message || 'Failed to load results');
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Initial fetch + whenever query in URL changes
//   useEffect(() => {
//     fetchResults(initialQuery);
//   }, [initialQuery]);

//   // Handle form submit / Enter key
//   const handleSearch = (e) => {
//     e.preventDefault();
//     const trimmed = query.trim();
//     if (!trimmed) return;

//     // Update URL params → triggers useEffect
//     setSearchParams({ q: trimmed });
//   };

//   return (
//     <main className={styles.main}>
//       <div className={styles.container}>
//         {/* Header */}
//         <header className={styles.pageHeader}>
//           <div className={styles.headerTop}>
//             <Link to="/" className={styles.backLink}>
//               <Icon icon="lucide:arrow-left" />
//               Back to Home
//             </Link>
//             <h1 className={styles.pageTitle}>Search Results</h1>
//           </div>

//           <p className={styles.resultsCount}>
//             Showing {loading ? '...' : results.length} of {loading ? '...' : 'many'} results for "{initialQuery || 'all resources'}"
//           </p>

//           {/* Working Search Bar */}
//           <form onSubmit={handleSearch} className={styles.searchBar}>
//             <input
//               type="text"
//               value={query}
//               onChange={(e) => setQuery(e.target.value)}
//               placeholder="Search resources..."
//               className={styles.searchInput}
//             />
//             <button type="submit" className={styles.searchBtn}>
//               <Icon icon="lucide:search" />
//             </button>
//           </form>
//         </header>

//         {/* Filters Toggle (mobile) */}
//         <button
//           className={styles.filterToggle}
//           onClick={() => setShowFilters(!showFilters)}
//         >
//           <Icon icon="lucide:filter" />
//           {showFilters ? 'Hide Filters' : 'Show Filters'}
//         </button>

//         <div className={`${styles.filters} ${showFilters ? styles.filtersOpen : ''}`}>
//           <div className={styles.filterGroup}>
//             <h3>Resource Type</h3>
//             <label><input type="checkbox" defaultChecked /> Guide</label>
//             <label><input type="checkbox" /> Template</label>
//             <label><input type="checkbox" /> Policy</label>
//             <label><input type="checkbox" /> Contact</label>
//           </div>

//           <div className={styles.filterGroup}>
//             <h3>Language</h3>
//             <label><input type="checkbox" defaultChecked /> English</label>
//             <label><input type="checkbox" /> Español</label>
//             <label><input type="checkbox" /> Français</label>
//             <label><input type="checkbox" /> العربية</label>
//           </div>

//           <div className={styles.filterGroup}>
//             <h3>Region</h3>
//             <label><input type="checkbox" defaultChecked /> Global</label>
//             <label><input type="checkbox" /> Africa</label>
//             <label><input type="checkbox" /> Latin America</label>
//             <label><input type="checkbox" /> Asia-Pacific</label>
//           </div>

//           <button className={styles.applyFilters}>Apply Filters</button>
//         </div>

//         {/* Results Grid */}
//         <section className={styles.resultsGrid}>
//           {loading ? (
//             <div className={styles.loading}>
//               <Icon icon="lucide:loader-2" className={styles.spinner} />
//               Searching...
//             </div>
//           ) : error ? (
//             <div className={styles.error}>
//               <Icon icon="lucide:alert-triangle" />
//               {error}
//             </div>
//           ) : results.length === 0 ? (
//             <div className={styles.empty}>
//               <Icon icon="lucide:search" />
//               <p>No results found for "{initialQuery}". Try different keywords.</p>
//             </div>
//           ) : (
//             results.map((res) => (
//               <Link to={`/resource/${res.id}`} key={res.id} className={styles.resultCard}>
//                 <div className={styles.cardImagePlaceholder}></div>

//                 <div className={styles.cardBody}>
//                   <div className={styles.cardTags}>
//                     <span className={styles.tag}>{res.type}</span>
//                     {/* category not in DB yet – optional */}
//                   </div>

//                   <h3 className={styles.cardTitle}>{res.title}</h3>
//                   <p className={styles.cardDescription}>
//                     {res.description.length > 120
//                       ? res.description.substring(0, 120) + '...'
//                       : res.description}
//                   </p>

//                   <div className={styles.cardMeta}>
//                     <span>From: {res.source || 'Community'}</span>
//                     <span>
//                       {res.rating ? `${res.rating} (${res.reviewCount || 0} reviews)` : 'No ratings'}
//                     </span>
//                     <span>{res.languages?.join(', ') || 'N/A'}</span>
//                     <span>{res.fileSize || res.fileType || 'N/A'}</span>
//                   </div>

//                   <div className={styles.cardFooter}>
//                     <button className={styles.actionBtnSmall}>
//                       <Icon icon="lucide:download" />
//                       Download
//                     </button>
//                     <button className={styles.actionBtnIcon}>
//                       <Icon icon="lucide:bookmark-plus" />
//                     </button>
//                   </div>
//                 </div>
//               </Link>
//             ))
//           )}
//         </section>

//         {/* Load More – placeholder */}
//         {!loading && results.length > 0 && (
//           <div className={styles.loadMore}>
//             <button className={styles.loadMoreBtn}>
//               Load More Results
//               <Icon icon="lucide:arrow-down" />
//             </button>
//             <p className={styles.paginationInfo}>
//               Showing {results.length} results
//             </p>
//           </div>
//         )}
//       </div>
//     </main>
//   );
// }
// import { Link, useSearchParams } from 'react-router-dom';
// import { Icon } from '@iconify/react';
// import { useState } from 'react';
// import styles from './SearchResults.module.css';

// // Dummy search results – in real app from API + query params
// const dummyResults = [
//   {
//     id: 1,
//     title: "Digital Security Checklist for High-Risk Activists (2026)",
//     description: "Step-by-step device hardening, secure messaging, threat modeling, and border crossing digital hygiene.",
//     type: "Guide",
//     source: "Global Rights Foundation",
//     rating: "4.8 (142 reviews)",
//     languages: ["EN", "ES", "FR", "AR"],
//     file: "PDF, 2.4 MB",
//     tags: ["Checklist", "High-Risk", "Encryption"],
//     link: "/resource/digital-checklist-2026"
//   },
//   {
//     id: 2,
//     title: "Organizational Security Policy Template",
//     description: "Customizable framework for emergency protocols, incident response, and organizational safeguards.",
//     type: "Template",
//     source: "SafeGuard Hub",
//     rating: "4.7 (89 reviews)",
//     languages: ["EN", "FR"],
//     file: "DOCX, 1.1 MB",
//     tags: ["Policy", "Template", "Organization"],
//     link: "/resource/policy-template"
//   },
//   {
//     id: 3,
//     title: "Emergency Digital Security Helpdesk Contacts",
//     description: "24/7 verified contacts for spyware infections, account takeovers, and urgent digital threats.",
//     type: "Contact",
//     source: "Access Now",
//     rating: "4.9 (203 ratings)",
//     languages: ["EN", "ES", "AR"],
//     file: "N/A (Web)",
//     tags: ["24/7", "Helpdesk", "Incident"],
//     link: "/support/access-now"
//   },
//   {
//     id: 4,
//     title: "Secure Communication Tools Comparison 2026",
//     description: "Up-to-date matrix comparing Signal, Session, Briar, Threema, and others for threat models.",
//     type: "Guide",
//     source: "EFF & Partners",
//     rating: "4.6 (112 reviews)",
//     languages: ["EN"],
//     file: "PDF, 3.8 MB",
//     tags: ["Messaging", "Comparison", "Encryption"],
//     link: "/resource/comm-tools-2026"
//   }
// ];

// export default function SearchResults() {
//   const [searchParams] = useSearchParams();
//   const query = searchParams.get('q') || 'digital security';

//   const [showFilters, setShowFilters] = useState(false);

//   return (
//     <main className={styles.main}>
//       <div className={styles.container}>
//         {/* Header */}
//         <header className={styles.pageHeader}>
//           <div className={styles.headerTop}>
//             <Link to="/" className={styles.backLink}>
//               <Icon icon="lucide:arrow-left" />
//               Back to Home
//             </Link>
//             <h1 className={styles.pageTitle}>Search Results</h1>
//           </div>

//           <p className={styles.resultsCount}>Showing {dummyResults.length} of 247 results for "{query}"</p>

//           <div className={styles.searchBar}>
//             <input
//               type="text"
//               defaultValue={query}
//               placeholder="Search resources..."
//               className={styles.searchInput}
//             />
//             <button className={styles.searchBtn}>
//               <Icon icon="lucide:search" />
//             </button>
//           </div>
//         </header>

//         {/* Filters Toggle (mobile) */}
//         <button
//           className={styles.filterToggle}
//           onClick={() => setShowFilters(!showFilters)}
//         >
//           <Icon icon="lucide:filter" />
//           {showFilters ? 'Hide Filters' : 'Show Filters'}
//         </button>

//         <div className={`${styles.filters} ${showFilters ? styles.filtersOpen : ''}`}>
//           <div className={styles.filterGroup}>
//             <h3>Resource Type</h3>
//             <label><input type="checkbox" defaultChecked /> Guide</label>
//             <label><input type="checkbox" /> Template</label>
//             <label><input type="checkbox" /> Policy</label>
//             <label><input type="checkbox" /> Contact</label>
//           </div>

//           <div className={styles.filterGroup}>
//             <h3>Language</h3>
//             <label><input type="checkbox" defaultChecked /> English</label>
//             <label><input type="checkbox" /> Español</label>
//             <label><input type="checkbox" /> Français</label>
//             <label><input type="checkbox" /> العربية</label>
//           </div>

//           <div className={styles.filterGroup}>
//             <h3>Region</h3>
//             <label><input type="checkbox" defaultChecked /> Global</label>
//             <label><input type="checkbox" /> Africa</label>
//             <label><input type="checkbox" /> Latin America</label>
//             <label><input type="checkbox" /> Asia-Pacific</label>
//           </div>

//           <button className={styles.applyFilters}>Apply Filters</button>
//         </div>

//         {/* Results Grid */}
//         <section className={styles.resultsGrid}>
//           {dummyResults.map((res) => (
//             <Link to={res.link} key={res.id} className={styles.resultCard}>
//               <div className={styles.cardImagePlaceholder}></div>

//               <div className={styles.cardBody}>
//                 <div className={styles.cardTags}>
//                   <span className={styles.tag}>{res.type}</span>
//                   <span className={styles.tag}>{res.category}</span>
//                 </div>

//                 <h3 className={styles.cardTitle}>{res.title}</h3>
//                 <p className={styles.cardDescription}>{res.description}</p>

//                 <div className={styles.cardMeta}>
//                   <span>From: {res.source}</span>
//                   <span>{res.rating}</span>
//                   <span>{res.languages.join(', ')}</span>
//                   <span>{res.file}</span>
//                 </div>

//                 <div className={styles.cardFooter}>
//                   <button className={styles.actionBtnSmall}>
//                     <Icon icon="lucide:download" />
//                     Download
//                   </button>
//                   <button className={styles.actionBtnIcon}>
//                     <Icon icon="lucide:bookmark-plus" />
//                   </button>
//                 </div>
//               </div>
//             </Link>
//           ))}
//         </section>

//         {/* Load More */}
//         <div className={styles.loadMore}>
//           <button className={styles.loadMoreBtn}>
//             Load More Results
//             <Icon icon="lucide:arrow-down" />
//           </button>
//           <p className={styles.paginationInfo}>
//             Showing {dummyResults.length} of 247 results
//           </p>
//         </div>
//       </div>
//     </main>
//   );
// }

// import { Icon } from '@iconify/react';
// import styles from './SearchResults.module.css';

// export default function SearchResults() {
//   return (
//     <main className={styles.main}>
//       <div className={styles.container}>
//         <header className={styles.searchHeader}>
//           <h1 className={styles.pageTitle}>Search Results</h1>
//           <div className={styles.searchBox}>
//             <input
//               type="text"
//               placeholder="Digital security for activists"
//               className={styles.searchInput}
//             />
//             <Icon icon="lucide:search" className={styles.searchIcon} />
//           </div>
//           <div className={styles.stats}>Showing 8 of 247 results</div>
//         </header>

//         <aside className={styles.filters}>
//           <div className={styles.filterGroup}>
//             <h3>Resource Type</h3>
//             <label><input type="checkbox" /> Guide</label>
//             <label><input type="checkbox" /> Template</label>
//             <label><input type="checkbox" /> Policy</label>
//             <label><input type="checkbox" /> Contact</label>
//           </div>

//           <div className={styles.filterGroup}>
//             <h3>Language</h3>
//             <label><input type="checkbox" /> English</label>
//             <label><input type="checkbox" /> Español</label>
//             <label><input type="checkbox" /> Français</label>
//             <label><input type="checkbox" /> العربية</label>
//           </div>

//           <div className={styles.filterGroup}>
//             <h3>Region</h3>
//             <label><input type="checkbox" /> Global</label>
//             <label><input type="checkbox" /> Africa</label>
//             <label><input type="checkbox" /> Latin America</label>
//           </div>
//         </aside>

//         <section className={styles.results}>
//           {/* Example result card – repeat / map in real app */}
//           <article className={styles.resultCard}>
//             <div className={styles.cardImagePlaceholder}></div>
//             <div className={styles.cardContent}>
//               <div className={styles.tags}>
//                 <span className={styles.tag}>Guide</span>
//                 <span className={styles.tag}>Digital Security</span>
//               </div>
//               <h3 className={styles.cardTitle}>Digital Security Checklist for High-Risk Activists</h3>
//               <p className={styles.cardDescription}>
//                 Comprehensive step-by-step guide covering device security, secure communications, and threat modeling.
//               </p>
//               <div className={styles.cardMeta}>
//                 <span>Global Rights Foundation</span>
//                 <span>4.8 (142 reviews)</span>
//                 <span>EN, ES, FR</span>
//               </div>
//               <div className={styles.cardActions}>
//                 <button className={styles.btnPrimary}>Download</button>
//                 <button className={styles.btnSecondary}>
//                   <Icon icon="lucide:bookmark" /> Save
//                 </button>
//               </div>
//             </div>
//           </article>

//           {/* ... more cards ... */}

//           <div className={styles.loadMore}>
//             <button className={styles.loadMoreBtn}>
//               Load More Results
//               <Icon icon="lucide:arrow-down" />
//             </button>
//           </div>
//         </section>
//       </div>
//     </main>
//   );
// }